'use client';

import { type PropsWithChildren, type CSSProperties } from 'react';

import { Button } from '@backyard-ui/core';
import { ChevronDown, ChevronUp } from '@backyard-ui/icons';
import * as Collapsible from '@radix-ui/react-collapsible';

import { styles } from './ShowMore.styles';
import { useShowMore } from './useShowMore';

interface ShowMoreProps extends PropsWithChildren {
  buttonAside?: boolean;
  initialHeight?: number;
}

function ShowMore(props: Readonly<ShowMoreProps>) {
  const { children, buttonAside = false, initialHeight = 40 } = props;

  const {
    isOpen,
    isCollapsible,
    isMarkerInView,
    isButtonDisabled,
    handleOpenChange,
    handleShowMoreClick,
    baseRef,
    contentRef,
    markerRef,
  } = useShowMore(initialHeight);

  const collapsibleClassNames = styles({
    isOpen,
    buttonAside,
    isButtonSticky: isOpen && !isMarkerInView,
  });

  const dynamicStyles = {
    '--initialHeight': `${initialHeight}px`,
  } as CSSProperties;

  return (
    <>
      <Collapsible.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        ref={baseRef}
        className={collapsibleClassNames.base()}
      >
        <Collapsible.Content forceMount>
          <div className={collapsibleClassNames.container()}>
            <div
              className={collapsibleClassNames.containerInner()}
              style={dynamicStyles}
            >
              <div ref={contentRef}>{children}</div>
            </div>
          </div>

          {isCollapsible && (
            <div
              className={collapsibleClassNames.buttonWrapper()}
              style={dynamicStyles}
            >
              <Collapsible.Trigger disabled={isButtonDisabled} asChild>
                <Button
                  size="xs"
                  variant="ghost"
                  iconRight={isOpen ? <ChevronUp /> : <ChevronDown />}
                  onClick={handleShowMoreClick}
                >
                  {isOpen ? 'Ver menos' : 'Ver mais'}
                </Button>
              </Collapsible.Trigger>
            </div>
          )}
        </Collapsible.Content>
      </Collapsible.Root>

      <div ref={markerRef} />
    </>
  );
}

export default ShowMore;
